/**
 * The day's "rest between sets", and everything that follows from it being a SEED rather than a
 * live default.
 *
 * The day carries `restSeconds`; **every set always carries its own concrete `prescribedRestTime`.**
 * Nothing downstream — the logger, the assigned plan, the session review — resolves rest through
 * the day. The day value is only ever STAMPED onto sets, and that makes four rules, all of which
 * live here.
 *
 * Ported from `move-with-me-frontend/.../plans/builder/model/dayRest.ts`, keeping its rule numbers
 * so the two can be diffed:
 *
 * · **D2** — changing the day's rest RESTAMPS every exercise that FOLLOWS it: one whose every set
 *   carries the day's current value. An exercise carrying anything else keeps every number the
 *   trainer authored. The decision reads the VALUES (`overridesDayRest`), never `showsRestColumn` —
 *   since D12 a superset member can be column-hidden while carrying an authored rest, so the column
 *   and "has its own rest" stopped being the same sentence, and keying on the column overwrote
 *   exactly the value D12 promised never to rewrite.
 * · **D3** — on LOAD the column state is DERIVED by comparison: shown iff any authored base set
 *   carries a rest differing from the day's. There is no wire flag for it.
 * · **D4** — REMOVING the column restamps that exercise's sets back to the day value. Otherwise D3
 *   would read them as still-overridden and the column would come straight back on the next load.
 * · **D12** — a superset member that does NOT close the round hides the column BY DEFAULT: its
 *   prescribed rest can never fire, because the logger's round-robin hands the cursor straight to
 *   the partner that still owes the round. A default, not a lock — the values behind it are never
 *   rewritten by anything but the two acts that always rewrite (D4's removal, D8's seeding).
 *
 * **`deriveRestColumns` closes PR-A1's recorded gap.** `responseToDraft` writes `false` on every
 * load because the derivation is only ever OBSERVED by the set table, which is this pull request.
 * The load seam now runs it, so a plan whose rests differ from its day's opens with the column
 * showing rather than hiding data the trainer authored on the phone.
 *
 * ── One divergence from the original, stated rather than hidden ──────────────────────────────
 * Mobile's `deriveRestColumns` takes an optional `previous` list so an edit re-derives only the
 * exercises whose POSITION moved, preserving a deliberate reveal elsewhere. That parameter is not
 * ported: it exists for the drag reorder, which is PR-E. Until a reorder exists, every caller is
 * either a load or a structural edit that establishes the day from scratch — which is exactly what
 * the parameter's absence means. It comes back with the drag.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { DayDraft, ExerciseDraft, SetDraft } from './planDraft.types';
import { isWarmupRole } from './setCounting';

/** The rest a day follows when it carries none — the seed for a new day and for a null wire value. */
export const DEFAULT_DAY_REST_SECONDS = 90;
/** Picker step, matching the logger's own ±15 s adjustment. */
export const DAY_REST_STEP_SECONDS = 15;
/** 0 is a real value ("no rest"); the wire allows up to 86400, the picker stops at ten minutes. */
export const MIN_DAY_REST_SECONDS = 0;
export const MAX_DAY_REST_SECONDS = 600;

/** The CONCRETE rest a day follows: its own value, or the flat default. A stored `0` stays `0`. */
export function dayRestSeconds(restSeconds: number | null | undefined): number {
  return restSeconds == null ? DEFAULT_DAY_REST_SECONDS : restSeconds;
}

/** Clamp a picked value into the picker's whole-second range. */
export function clampDayRest(seconds: number): number {
  const whole = Math.round(seconds);
  return Math.min(MAX_DAY_REST_SECONDS, Math.max(MIN_DAY_REST_SECONDS, whole));
}

/**
 * The value a rest CELL commits: the number the trainer typed, or — when they cleared it — the
 * day's own rest.
 *
 * Under the seed semantics a blank rest has no meaning: every set always carries a concrete number.
 * Committing `null` instead would ship the reader's hardcoded default whatever the day says, AND
 * read as a permanent override below, so the column could never be hidden or restamped again.
 *
 * An explicit `0` passes straight through — that is "no rest", a real prescription.
 */
export function restCellSeconds(parsed: number | null, dayRest: number): number {
  return parsed ?? dayRest;
}

/**
 * D3 — does this exercise's authored base override the day's rest?
 *
 * The comparison is STRICT, so a set with no rest at all counts as an override: its cell renders
 * empty, which is genuinely a different prescription from "90 s", and hiding the column would leave
 * the trainer unable to see or fix it. Such a set can only arrive from outside the builder — inside
 * it, clearing a cell commits the day's rest precisely so a backspace cannot mint one.
 */
export function overridesDayRest(sets: readonly SetDraft[], dayRest: number): boolean {
  return sets.some((set) => set.prescribedRestTime !== dayRest);
}

/**
 * D12 — the exercises in a day whose prescribed rest can NEVER fire: superset members that do not
 * close the round.
 *
 * Membership is CONTIGUOUS exercises sharing a non-empty group, work-only, two or more — the same
 * rule `weekProjection` brackets by. Re-deriving it differently here is how the builder's bracket
 * and the logger's cursor drift apart. Returned as slot tokens, this app's exercise address.
 */
function supersetMembersWithoutRest(exercises: readonly ExerciseDraft[]): Set<string> {
  const silent = new Set<string>();
  let index = 0;
  while (index < exercises.length) {
    const head = exercises[index];
    if (head === undefined) break;
    // Truthy, not `!= null`. `group: ""` is UNGROUPED — the backend's column is `string?` with no
    // non-empty validator, and a port written as `group != null` fuses two empty-grouped
    // neighbours into a superset the phone would draw as two singles.
    if (!head.group) {
      index += 1;
      continue;
    }
    let next = index + 1;
    while (next < exercises.length && exercises[next]?.group === head.group) next += 1;
    const members = exercises.slice(index, next);
    const work = members.filter((member) => !isWarmupRole(member.role));
    // A group carrying a warm-up is a mini-group, not a superset: its rest fires normally.
    if (work.length === members.length && work.length >= 2) {
      for (const member of work.slice(0, -1)) silent.add(member.slotToken);
    }
    index = next;
  }
  return silent;
}

/**
 * D3 ∘ D12 — resolve the rest column across ONE day's exercises.
 *
 * The ORDER is the whole point: D3 derives the column from the values, and D12 then VETOES it on a
 * member whose rest can never fire. Composing them the other way round — deriving per exercise,
 * then hiding at render time — would make a deliberate reveal indistinguishable from a derived one,
 * so the column could never be brought back.
 *
 * **The veto never touches the SETS.** A hidden member keeps the rest it was authored with; that
 * number is what a ragged tail still prescribes, and it is the trainer's data rather than a display
 * artefact. Idempotent.
 */
export function deriveRestColumns(
  exercises: readonly ExerciseDraft[],
  dayRest: number,
): ExerciseDraft[] {
  const silent = supersetMembersWithoutRest(exercises);
  return exercises.map((exercise) => {
    const shown = !silent.has(exercise.slotToken) && overridesDayRest(exercise.sets, dayRest);
    return exercise.showsRestColumn === shown ? exercise : { ...exercise, showsRestColumn: shown };
  });
}

/**
 * Stamp `dayRest` onto every one of an exercise's sets — the single write behind three acts:
 * removing the rest column (D4), seeding a freshly-added exercise from its day (D8), and the
 * per-exercise step of a day restamp (D2). No warm-up carve-out: a warm row's rest is stamped like
 * every other.
 *
 * Returns the same exercise by reference when nothing changes, so a no-op cannot make an untouched
 * draft read as edited.
 */
export function restampExerciseRest(exercise: ExerciseDraft, dayRest: number): ExerciseDraft {
  if (!overridesDayRest(exercise.sets, dayRest)) return exercise;
  return {
    ...exercise,
    sets: exercise.sets.map((set) =>
      set.prescribedRestTime === dayRest ? set : { ...set, prescribedRestTime: dayRest },
    ),
  };
}

/** How a day-rest change lands — the partition the restamp makes, from the same question. */
export interface DayRestRestampSummary {
  /** Exercises that TOOK the new value — they were following the day. */
  readonly restamped: number;
  /** Exercises left alone because their sets carry their own rest. */
  readonly skipped: number;
}

/**
 * Count how a day-rest change lands.
 *
 * **ORDERING**: this must be handed the day as it stood BEFORE the restamp, which is why it reads
 * the day's own `restSeconds` rather than taking one — so a caller cannot pair a post-restamp day
 * with a pre-restamp number. Afterwards the answer is worthless: every follower has been rewritten
 * to the new value, and an override that happens to equal it can no longer be told from one.
 */
export function dayRestRestampSummary(
  day: Pick<DayDraft, 'exercises' | 'restSeconds'>,
): DayRestRestampSummary {
  const current = dayRestSeconds(day.restSeconds);
  let restamped = 0;
  let skipped = 0;
  for (const exercise of day.exercises) {
    if (overridesDayRest(exercise.sets, current)) skipped += 1;
    else restamped += 1;
  }
  return { restamped, skipped };
}
